// app/javascript/controllers/analytics_controller.js
import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

export default class extends Controller {
  static targets = ["line", "bar"];
  static values = {
    lineLabels: Array,
    lineData: Array,
    barLabels: Array,
    barData: Array,
  };

  initialize() {
    this._beforeCache = this.beforeCache.bind(this);
  }

  connect() {
    document.addEventListener("turbo:before-cache", this._beforeCache);
    // Construir gráficos
    this.#buildLine();
    this.#buildBar();

    // Redimensionar si cambia el contenedor (ej. sidebar colapsa)
    this._ro = new ResizeObserver(() => {
      this._lineChart?.resize();
      this._barChart?.resize();
    });
    if (this.hasLineTarget) this._ro.observe(this.lineTarget.parentElement);
    if (this.hasBarTarget) this._ro.observe(this.barTarget.parentElement);
  }

  disconnect() {
    this.#destroyCharts();
    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }
    document.removeEventListener("turbo:before-cache", this._beforeCache);
  }

  beforeCache() {
    // Turbo serializa el DOM: destruye instancias para no duplicar al volver
    this.#destroyCharts();
  }

  #destroyCharts() {
    if (this._lineChart) {
      this._lineChart.destroy();
      this._lineChart = null;
    }
    if (this._barChart) {
      this._barChart.destroy();
      this._barChart = null;
    }
  }

  // Asegura que el canvas tenga dimensiones reales antes de crear el chart
  #ensureCanvasSize(canvas) {
    const box = canvas.parentElement; // el div .relative h-48
    // Si por cualquier motivo el parent tiene 0, fuerza algo razonable
    const w = Math.max(1, box.clientWidth);
    const h = Math.max(1, box.clientHeight);
    canvas.width = w;
    canvas.height = h;
  }

  #buildLine() {
    if (!this.hasLineTarget) return;
    if (this._lineChart) {
      this._lineChart.destroy();
      this._lineChart = null;
    }

    this.#ensureCanvasSize(this.lineTarget);
    const ctx = this.lineTarget.getContext("2d");

    this._lineChart = new Chart(ctx, {
      type: "line",
      data: {
        labels: this.lineLabelsValue,
        datasets: [
          {
            label: "Escaneos",
            data: this.lineDataValue,
            tension: 0.3,
            pointRadius: 4,
            pointBackgroundColor: "#ffcc00", // color puntos
            borderColor: "#ffcc00", // línea
            backgroundColor: "rgba(255, 204, 0, 0.2)", // área bajo la curva
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { maxRotation: 0, autoSkip: true } },
          y: { beginAtZero: true, precision: 0 },
        },
      },
    });
  }

  #buildBar() {
    if (!this.hasBarTarget) return;
    if (this._barChart) {
      this._barChart.destroy();
      this._barChart = null;
    }

    this.#ensureCanvasSize(this.barTarget);
    const ctx = this.barTarget.getContext("2d");

    this._barChart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: this.barLabelsValue,
        datasets: [
          {
            label: "Escaneos",
            data: this.barDataValue,
            borderWidth: 1,
            backgroundColor: ["#ffcc00", "#999999"], // cada barra color distinto
            borderColor: "#333333",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { autoSkip: false, maxRotation: 0 } },
          y: { beginAtZero: true, precision: 0 },
        },
      },
    });
  }
}
